/**
 * Advanced connectivity dock (S2): Coherence, PLI, connectivity map.
 * Assistive decision-support only — confirm on raw EEG.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  getConnectivityMap,
  postCoherence,
  postPli,
  type CoherenceResult,
  type ConnectivityMapResult,
  type PliResult,
} from "../services/advancedAnalysisApi";
import QEEGMetricsService from "../services/QEEGMetrics";

export const CONNECTIVITY_INTENDED_USE =
  "Coherence, PLI, and connectivity maps are non-diagnostic decision-support aids for navigating raw EEG relationships. They do not provide standalone clinical interpretation.";

type TabKey = "coherence" | "pli" | "map";

export type AdvancedConnectivityPanelProps = {
  recordingId?: string | null;
  channelNames?: string[];
  leftChannelData?: Float32Array | number[];
  rightChannelData?: Float32Array | number[];
  sampleRate?: number;
};

function pickDefaultPair(names: string[]): [string, string] {
  if (names.length >= 2) return [names[0], names[1]];
  if (names.length === 1) return [names[0], names[0]];
  return ["C3", "C4"];
}

export default function AdvancedConnectivityPanel({
  recordingId,
  channelNames = [],
  leftChannelData,
  rightChannelData,
  sampleRate = 256,
}: AdvancedConnectivityPanelProps) {
  const names = channelNames.length ? channelNames : ["C3", "C4"];
  const defaults = pickDefaultPair(names);
  const [tab, setTab] = useState<TabKey>("coherence");
  const [channel1, setChannel1] = useState(defaults[0]);
  const [channel2, setChannel2] = useState(defaults[1]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<"server" | "local-fallback" | null>(null);
  const [coherence, setCoherence] = useState<CoherenceResult | null>(null);
  const [pli, setPli] = useState<PliResult | null>(null);
  const [map, setMap] = useState<ConnectivityMapResult | null>(null);

  useEffect(() => {
    setChannel1(defaults[0]);
    setChannel2(defaults[1]);
  }, [names.join("|")]);

  const run = async () => {
    if (!recordingId) {
      setError("Recording id required for server connectivity analysis");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (tab === "coherence") {
        try {
          const result = await postCoherence({
            recordingId,
            channel1,
            channel2,
          });
          setCoherence(result);
          setSource("server");
        } catch {
          if (leftChannelData?.length && rightChannelData?.length) {
            const svc = new QEEGMetricsService();
            const left = Array.from(leftChannelData as ArrayLike<number>);
            const right = Array.from(rightChannelData as ArrayLike<number>);
            const n = Math.min(left.length, right.length);
            const local = svc.computeCoherence(
              left.slice(0, n),
              right.slice(0, n),
              sampleRate,
              [channel1, channel2],
            );
            setCoherence({
              recordingId,
              channelPair: [channel1, channel2],
              frequencies: local.frequency || [],
              coherence: local.coherence || [],
              meanCoherence: local.meanCoherence?.alpha ?? local.peakCoherence ?? 0,
            });
            setSource("local-fallback");
          } else {
            throw new Error("Server coherence unavailable and no local pair");
          }
        }
      } else if (tab === "pli") {
        const result = await postPli({ recordingId, channel1, channel2 });
        setPli(result);
        setSource("server");
      } else {
        const result = await getConnectivityMap(recordingId, "pli");
        setMap(result);
        setSource("server");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Connectivity analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const series = useMemo(() => {
    if (tab === "coherence" && coherence) {
      return coherence.frequencies.map((hz, i) => ({
        hz,
        value: coherence.coherence[i] ?? 0,
      }));
    }
    if (tab === "pli" && pli) {
      return pli.frequencies.map((hz, i) => ({
        hz,
        value: pli.pli[i] ?? 0,
      }));
    }
    return [];
  }, [tab, coherence, pli]);

  return (
    <div
      className="eeg-advanced-connectivity-panel"
      data-testid="advanced-connectivity-panel"
    >
      <div className="eeg-dock-panel-chrome__note" role="note">
        <strong>Intended use:</strong> {CONNECTIVITY_INTENDED_USE}
      </div>
      <div className="eeg-dock-panel-chrome__toolbar eeg-advanced-connectivity-panel__tabs">
        {(["coherence", "pli", "map"] as TabKey[]).map((key) => (
          <button
            key={key}
            type="button"
            className={tab === key ? "is-active" : undefined}
            aria-pressed={tab === key}
            onClick={() => setTab(key)}
          >
            {key === "map" ? "Connectivity map" : key.toUpperCase()}
          </button>
        ))}
      </div>
      {tab !== "map" && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "8px 0" }}>
          <label>
            Channel A{" "}
            <select
              value={channel1}
              onChange={(e) => setChannel1(e.target.value)}
              data-testid="connectivity-channel-a"
            >
              {names.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <label>
            Channel B{" "}
            <select
              value={channel2}
              onChange={(e) => setChannel2(e.target.value)}
              data-testid="connectivity-channel-b"
            >
              {names.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      <button
        type="button"
        onClick={() => void run()}
        disabled={loading || !recordingId}
        data-testid="connectivity-run"
      >
        {loading ? "Running…" : "Run analysis"}
      </button>
      {source && (
        <div data-testid="connectivity-source">Source: {source}</div>
      )}
      {error && (
        <div role="alert" data-testid="connectivity-error">
          {error}
        </div>
      )}
      {tab !== "map" && series.length > 0 && (
        <div data-testid="connectivity-series">
          <div>
            Mean:{" "}
            {tab === "coherence"
              ? coherence?.meanCoherence?.toFixed(3)
              : pli?.meanPLI?.toFixed(3)}
          </div>
          <ul style={{ maxHeight: 160, overflow: "auto", fontSize: 12 }}>
            {series.slice(0, 40).map((row) => (
              <li key={`${row.hz}`}>
                {row.hz.toFixed(1)} Hz → {row.value.toFixed(3)}
              </li>
            ))}
          </ul>
        </div>
      )}
      {tab === "map" && map && (
        <div data-testid="connectivity-map">
          <div>
            Method: {map.method} · {map.channels.length} channels
          </div>
          <pre style={{ fontSize: 10, maxHeight: 200, overflow: "auto" }}>
            {map.connectivityMatrix
              .slice(0, 8)
              .map((row) =>
                row
                  .slice(0, 8)
                  .map((v) => v.toFixed(2))
                  .join(" "),
              )
              .join("\n")}
          </pre>
        </div>
      )}
    </div>
  );
}
